import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Logo — monogramme « AL » géométrique (style origami).
//
// Le tracé est un CARRÉ PLEIN avec le « AL » en NÉGATIF (creux). Deux couleurs,
// gérées ENTIÈREMENT dans le SVG (aucun fond sur le conteneur) :
//   • `fill-paper` : le carré (épouse le fond papier → invisible sur la navbar) ;
//   • `fill-ink`   : un rectangle d'encre ENCASTRÉ, placé DERRIÈRE le carré et
//                    visible uniquement à travers les creux = le monogramme.
//
// Pourquoi un rectangle encastré plutôt qu'un `bg-ink` sur le conteneur ?
// Au survol le logo TOURNE : un fond sombre pleine taille transparaîtrait par les
// bords anticrénelés du carré papier → un « carré » fantôme tournant. Ici l'encre
// est confinée à l'intérieur (la zone du AL mesure 411×319 dans un viewBox 547,
// soit ≥68px de marge opaque) : les bords du carré n'ont rien de sombre derrière
// eux, donc rien ne bave en rotation.
//
// Inversion clair/sombre automatique via les variables CSS du thème
//   clair : carré blanc + AL noir   ·   sombre : carré noir + AL blanc.
// ─────────────────────────────────────────────────────────────────────────────

interface LogoProps {
  /** Classe de taille (défaut : 28px, calibré pour la navbar). */
  className?: string;
}

export const Logo: React.FC<LogoProps> = ({ className = 'h-7 w-7' }) => (
  <svg
    className={className}
    viewBox="0 0 547 547"
    aria-hidden="true"
    focusable="false"
  >
    {/* Encre visible uniquement à travers les creux (monogramme), encastrée loin
        des bords pour ne jamais transparaître pendant la rotation. */}
    <rect x="40" y="88" width="467" height="371" className="fill-ink" />
    {/* Carré papier plein + accents (creux = AL laissant voir l'encre dessous). */}
    <g transform="translate(0,547) scale(0.1,-0.1)" className="fill-paper" stroke="none">
      <path d="M0 2735 l0 -2735 2735 0 2735 0 0 2735 0 2735 -2735 0 -2735 0 0
-2735z m2417 1533 c-54 -97 -164 -300 -238 -438 -37 -68 -71 -126 -76 -127 -4
-1 -44 64 -87 145 -44 81 -119 220 -168 309 -48 89 -88 165 -88 168 0 3 155 5
345 5 l346 0 -34 -62z m853 -181 l385 -196 2 -929 c2 -511 -1 -955 -5 -986
l-7 -56 -73 177 -72 178 0 718 c0 394 -3 717 -7 717 -5 -1 -89 -118 -188 -260
-98 -143 -182 -256 -186 -252 -11 12 -59 141 -59 158 0 8 74 122 165 252 l165
236 -134 70 c-74 39 -211 109 -305 156 l-171 85 0 87 0 88 53 -24 c28 -13 225
-111 437 -219z m-1401 -192 c110 -203 201 -377 201 -386 0 -9 -61 -171 -136
-360 -74 -189 -150 -382 -167 -427 l-32 -84 -300 -203 c-492 -333 -749 -504
-752 -502 -2 3 157 393 397 972 70 171 176 427 235 570 59 143 124 301 145
350 21 50 70 169 110 265 83 200 80 194 90 183 4 -4 98 -174 209 -378z m773
173 c48 -117 123 -301 168 -408 45 -107 99 -240 121 -295 40 -97 188 -457 314
-760 34 -82 88 -213 120 -290 32 -77 82 -198 112 -269 l55 -129 -43 -61 c-23
-34 -127 -187 -232 -341 -105 -154 -192 -282 -194 -285 -7 -8 -16 12 -243 560
-124 297 -227 540 -230 540 -3 0 -74 -92 -159 -205 l-153 -205 -418 0 -418 0
-64 -152 c-36 -84 -109 -260 -164 -390 -54 -131 -102 -238 -105 -238 -7 0
-417 640 -420 655 -1 6 26 28 61 50 58 36 64 38 74 22 56 -92 249 -383 255
-385 4 -1 25 39 46 90 177 432 289 696 301 715 7 12 108 85 223 162 l210 141
312 0 311 0 -5 23 c-2 12 -82 220 -177 461 l-172 439 23 41 c23 41 40 72 274
504 66 122 122 222 125 222 4 0 45 -96 92 -212z m2148 -2173 c0 -2 -63 -107
-141 -232 -78 -126 -179 -290 -226 -365 l-85 -138 -595 0 c-473 0 -593 3 -587
13 11 17 401 588 454 665 l44 62 568 0 c312 0 568 -2 568 -5z" />
      <path d="M1575 3683 c-130 -316 -155 -377 -270 -653 -53 -129 -117 -282 -140
-340 -24 -58 -66 -159 -94 -224 -28 -66 -50 -121 -48 -122 1 -1 130 85 287
190 l285 193 153 384 c83 211 152 389 152 397 0 17 -215 412 -225 412 -1 0
-46 -107 -100 -237z" />
      <path d="M2435 3758 c-48 -90 -99 -183 -112 -208 l-25 -45 102 -255 c120 -302
242 -603 247 -609 2 -3 9 3 16 11 6 9 68 86 135 173 l124 156 -65 157 c-124
298 -244 587 -283 684 -21 54 -42 98 -45 98 -3 0 -45 -73 -94 -162z" />
      <path d="M2863 2649 c-65 -82 -124 -160 -132 -172 -14 -23 -24 3 236 -627 95
-231 114 -275 122 -283 8 -8 261 353 261 371 0 4 -42 109 -94 232 -51 124
-131 316 -177 428 -46 111 -87 202 -91 202 -4 0 -60 -68 -125 -151z" />
      <path d="M1644 2398 c-10 -18 -43 -96 -75 -173 l-58 -140 337 -3 c185 -1 342
0 348 2 7 3 63 76 125 163 62 87 117 164 122 171 7 9 -71 12 -387 12 l-395 0
-17 -32z" />
      <path d="M3721 1730 c-11 -7 -260 -375 -267 -394 -6 -14 32 -16 394 -16 l401
0 19 28 c26 37 232 372 232 377 0 6 -770 11 -779 5z" />
    </g>
  </svg>
);

export default Logo;
